import React, { useState } from "react";
import { Modal, View, Text, Pressable, ScrollView, StyleSheet } from "react-native";
import { useUser } from "../../Widgets/UserContext";
import { useSpotlightTutorial } from "../../Widgets/SpotlightTutorial/SpotlightTutorialProvider";
import { TUTORIAL_CHAPTERS, getChapter } from "../../Widgets/SpotlightTutorial/tutorialRegistry";
import { SPOTLIGHT_COLORS } from "../../Widgets/SpotlightTutorial/spotlightTheme";
import { formatReferralForDisplay } from "../../constants/ReferralSources";
import { isChapterAvailableForUser, restartTutorialForUser, resetTutorialForUser } from "../../services/SpotlightTutorialAdminService";

const getChapterTitle = (chapterId) => getChapter(chapterId)?.title || chapterId || "—";

const formatTutorialStatus = (tutorialState) => {
	if (!tutorialState?.status) return "Not set";
	const position = tutorialState.currentChapterId ? ` (${getChapterTitle(tutorialState.currentChapterId)} / ${tutorialState.currentStepId})` : "";
	return `${tutorialState.status}${position}`;
};

const formatCompletedChapters = (tutorialState) => {
	const completed = tutorialState?.completedChapters || [];
	return completed.length ? completed.map(getChapterTitle).join(", ") : "None";
};

const InfoRow = ({ label, value }) => (
	<View style={styles.infoRow}>
		<Text style={styles.infoLabel}>{label}</Text>
		<Text style={styles.infoValue}>{value || "—"}</Text>
	</View>
);

const ActionButton = ({ label, onPress, disabled, variant = "primary" }) => (
	<Pressable onPress={onPress} disabled={disabled} style={[styles.button, styles[variant], disabled && styles.buttonDisabled]}>
		<Text style={[styles.buttonText, variant === "secondary" && styles.secondaryText]}>{label}</Text>
	</Pressable>
);

const SpotlightTutorialAdminModal = ({ user, onClose, onUserUpdated }) => {
	const { currentUser } = useUser();
	const { startPreview } = useSpotlightTutorial();
	const [selectedChapterId, setSelectedChapterId] = useState(TUTORIAL_CHAPTERS[0].id);
	const [askReferralAgain, setAskReferralAgain] = useState(false);
	const [isBusy, setIsBusy] = useState(false);
	const [message, setMessage] = useState("");

	if (!user) return null;

	const selectedChapter = getChapter(selectedChapterId);
	const isSelectedChapterAvailable = isChapterAvailableForUser(selectedChapter, user);

	const runAction = async (action, successMessage) => {
		setIsBusy(true);
		setMessage("");
		try {
			onUserUpdated(user.id, await action());
			setMessage(successMessage);
		} catch (error) {
			setMessage(`Failed: ${error.message}`);
		} finally {
			setIsBusy(false);
		}
	};

	const handleRestart = () =>
		runAction(
			() => restartTutorialForUser(user, { chapter: selectedChapter, adminEmail: currentUser?.email }),
			`Tutorial restarted at "${selectedChapter.title}". It starts the next time ${user.email} loads the app.`
		);

	const handleReset = () => {
		if (!window.confirm(`Reset tutorial progress for ${user.email}? Their account, profile, Stripe, Registry and Event data are not touched.`)) return;
		runAction(() => resetTutorialForUser(user, { adminEmail: currentUser?.email, askReferralAgain }), `Tutorial progress reset. It launches automatically the next time ${user.email} opens the Hub or My Profile.`);
	};

	const handlePreview = () => {
		startPreview(selectedChapterId);
		onClose();
	};

	return (
		<Modal visible transparent animationType="fade" onRequestClose={onClose}>
			<View style={styles.overlay}>
				<View style={styles.card}>
					<Text style={styles.title}>Spotlight Tutorial: {user.email}</Text>
					<ScrollView>
						<InfoRow label="Account type" value={user.accountType} />
						<InfoRow label="How they heard about us" value={formatReferralForDisplay(user)} />
						<InfoRow label="Tutorial status" value={formatTutorialStatus(user.spotlightTutorial)} />
						<InfoRow label="Completed chapters" value={formatCompletedChapters(user.spotlightTutorial)} />

						<Text style={styles.sectionTitle}>Chapter</Text>
						<View style={styles.chapterList}>
							{TUTORIAL_CHAPTERS.map((chapter) => (
								<Pressable
									key={chapter.id}
									onPress={() => setSelectedChapterId(chapter.id)}
									style={[styles.chapterChip, chapter.id === selectedChapterId && styles.chapterChipSelected]}
								>
									<Text style={[styles.chapterChipText, chapter.id === selectedChapterId && styles.chapterChipTextSelected]}>{chapter.title}</Text>
								</Pressable>
							))}
						</View>
						{!isSelectedChapterAvailable && <Text style={styles.note}>This chapter only runs for Graduate accounts, so this user can't restart it. Use Preview instead.</Text>}

						<Pressable onPress={() => setAskReferralAgain((previous) => !previous)} style={styles.checkboxRow}>
							<View style={[styles.checkbox, askReferralAgain && styles.checkboxChecked]} />
							<Text style={styles.infoValue}>When resetting, also ask "How did you hear about us?" again</Text>
						</Pressable>

						<View style={styles.actions}>
							<ActionButton label="Restart at chapter" onPress={handleRestart} disabled={isBusy || !isSelectedChapterAvailable} />
							<ActionButton label="Reset progress" onPress={handleReset} disabled={isBusy} variant="danger" />
							<ActionButton label="Preview as me" onPress={handlePreview} disabled={isBusy} variant="secondary" />
						</View>
						<Text style={styles.note}>
							Preview runs in your own session and saves no tutorial progress, but interactive steps (like changing account type) act on the account you are logged in as.
							Restart and Reset only change tutorial and onboarding state.
						</Text>
						{message ? <Text style={styles.message}>{message}</Text> : null}
					</ScrollView>
					<ActionButton label="Close" onPress={onClose} variant="secondary" />
				</View>
			</View>
		</Modal>
	);
};

const styles = StyleSheet.create({
	overlay: { flex: 1, backgroundColor: "rgba(0,0,0,0.5)", justifyContent: "center", alignItems: "center", padding: 16 },
	card: { width: "100%", maxWidth: 560, maxHeight: "90%", backgroundColor: SPOTLIGHT_COLORS.cream, borderRadius: 16, padding: 20, gap: 12 },
	title: { fontSize: 18, fontWeight: "bold", color: SPOTLIGHT_COLORS.greenDark },
	sectionTitle: { marginTop: 12, marginBottom: 6, fontWeight: "bold", color: SPOTLIGHT_COLORS.greenDark },
	infoRow: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 4, gap: 12 },
	infoLabel: { color: SPOTLIGHT_COLORS.mutedText },
	infoValue: { flexShrink: 1, textAlign: "right", color: SPOTLIGHT_COLORS.text, fontWeight: "600" },
	checkboxRow: { flexDirection: "row", alignItems: "center", gap: 8, marginTop: 14 },
	checkbox: { width: 18, height: 18, borderRadius: 4, borderWidth: 2, borderColor: SPOTLIGHT_COLORS.green },
	checkboxChecked: { backgroundColor: SPOTLIGHT_COLORS.green },
	chapterList: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
	chapterChip: { paddingVertical: 6, paddingHorizontal: 12, borderRadius: 16, borderWidth: 1, borderColor: SPOTLIGHT_COLORS.green },
	chapterChipSelected: { backgroundColor: SPOTLIGHT_COLORS.green },
	chapterChipText: { color: SPOTLIGHT_COLORS.greenDark },
	chapterChipTextSelected: { color: SPOTLIGHT_COLORS.white },
	actions: { flexDirection: "row", flexWrap: "wrap", gap: 8, marginTop: 14 },
	button: { paddingVertical: 10, paddingHorizontal: 14, borderRadius: 10, alignItems: "center" },
	primary: { backgroundColor: SPOTLIGHT_COLORS.green },
	danger: { backgroundColor: "#cc0000" },
	secondary: { backgroundColor: SPOTLIGHT_COLORS.creamDeep, borderWidth: 1, borderColor: SPOTLIGHT_COLORS.green },
	buttonDisabled: { opacity: 0.5 },
	buttonText: { color: SPOTLIGHT_COLORS.white, fontWeight: "bold" },
	secondaryText: { color: SPOTLIGHT_COLORS.greenDark },
	note: { marginTop: 10, fontSize: 12, color: SPOTLIGHT_COLORS.mutedText },
	message: { marginTop: 10, color: SPOTLIGHT_COLORS.greenDark, fontWeight: "600" },
});

export default SpotlightTutorialAdminModal;
